import { useEffect, useRef, useState } from 'react'
import { Chart, registerables } from 'chart.js'
import { colors, fonts } from './UI'
import { useTheme } from '../lib/theme'

Chart.register(...registerables)

// Normalise any snapshot key to YYYY-MM-DD so legacy 'YYYY-MM' rows sort/compare
// correctly against daily keys.
function normaliseKey(key) {
  if (!key) return ''
  return key.length === 7 ? `${key}-01` : key
}
function formatDayLabel(key) {
  const k = normaliseKey(key)
  const [y, m, d] = k.split('-').map(Number)
  const date = new Date(y, (m || 1) - 1, d || 1)
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short' })
}
function formatAxisGBP(v) {
  const n = Math.abs(v)
  if (n >= 1_000_000) return '£' + (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'm'
  if (n >= 10_000) return '£' + Math.round(v / 1000) + 'k'
  if (n >= 1_000) return '£' + (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return '£' + Math.round(v)
}

const NW_PERIODS = ['1W', '1M', '3M', '6M', 'YTD', '1Y', 'ALL']

function filterHistory(history, period) {
  if (!history || !history.length) return []
  const now = new Date()
  const keyOf = h => normaliseKey(h.month)
  if (period === '1W') {
    const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7)
    const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth()+1).padStart(2,'0')}-${String(cutoff.getDate()).padStart(2,'0')}`
    return history.filter(h => keyOf(h) >= cutoffStr)
  }
  if (period === 'YTD') {
    const cutoffStr = `${now.getFullYear()}-01-01`
    return history.filter(h => keyOf(h) >= cutoffStr)
  }
  const cutoffs = { '1M': 1, '3M': 3, '6M': 6, '1Y': 12, 'ALL': 999 }
  const months = cutoffs[period] || 6
  if (period === 'ALL') return history.slice()
  const cutoff = new Date(now.getFullYear(), now.getMonth() - months + 1, 1)
  const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth()+1).padStart(2,'0')}-01`
  return history.filter(h => keyOf(h) >= cutoffStr)
}

function aggregateHistory(filtered, period) {
  const sorted = filtered.slice().sort((a, b) => normaliseKey(a.month).localeCompare(normaliseKey(b.month)))
  if (period === '1W' || period === '1M' || period === 'YTD') return sorted
  if (period === '3M' || period === '6M') {
    const weeks = {}
    sorted.forEach(h => {
      const [y, m, d] = normaliseKey(h.month).split('-').map(Number)
      const weekKey = `${y}-${String(m).padStart(2,'0')}-W${Math.ceil((d || 1) / 7)}`
      weeks[weekKey] = h
    })
    return Object.values(weeks)
  }
  const months = {}
  sorted.forEach(h => {
    const monthKey = normaliseKey(h.month).slice(0, 7)
    months[monthKey] = h
  })
  return Object.values(months)
}

export default function NetWorthChart({ history }) {
  const ref = useRef(null)
  const chart = useRef(null)
  const { themeId } = useTheme()
  const [period, setPeriod] = useState('6M')
  const [tab, setTab] = useState('networth')
  const [singlePoint, setSinglePoint] = useState(false)

  useEffect(() => {
    if (!ref.current) return
    const filtered = filterHistory(history, period)
    const aggregated = aggregateHistory(filtered, period)
    setSinglePoint(aggregated.length === 1)
    if (aggregated.length === 0) { if (chart.current) { chart.current.destroy(); chart.current = null } return }
    if (chart.current) chart.current.destroy()

    const dataKey = tab === 'assets' ? 'total_assets' : tab === 'debt' ? 'total_debts' : 'net_worth'
    const lineColor = tab === 'debt' ? colors.red : colors.gold
    const values = aggregated.map(h => tab === 'debt' ? Math.abs(h[dataKey] || 0) : (h[dataKey] || 0))

    let yMin, yMax
    if (values.length === 1) {
      const v = values[0]
      const pad = Math.max(Math.abs(v) * 0.25, 500)
      yMin = Math.max(0, v - pad)
      yMax = v + pad
    }

    chart.current = new Chart(ref.current, {
      type: 'line',
      data: {
        labels: aggregated.map(h => formatDayLabel(h.month)),
        datasets: [{
          data: values,
          borderColor: lineColor,
          backgroundColor: tab === 'debt' ? 'rgba(224,82,82,0.06)' : 'rgba(201,151,43,0.06)',
          fill: true, tension: 0.4,
          pointRadius: aggregated.length === 1 ? 5 : (aggregated.length > 10 ? 1 : 3),
          pointBackgroundColor: lineColor, borderWidth: 2,
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: colors.muted, font: { size: 10, family: fonts.body }, maxTicksLimit: 6 } },
          y: { min: yMin, max: yMax, grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: colors.muted, font: { size: 11 }, maxTicksLimit: 5, callback: formatAxisGBP } }
        }
      }
    })
    return () => { if (chart.current) chart.current.destroy() }
  }, [history, period, tab, themeId])

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '2px', marginBottom: '10px', flexWrap: 'wrap' }}>
        {NW_PERIODS.map(p => (
          <button key={p} onClick={() => setPeriod(p)} style={{ padding: '4px 8px', fontSize: '14px', fontFamily: fonts.body, fontWeight: 600, border: 'none', borderRadius: '2px', cursor: 'pointer', background: period === p ? 'rgba(201,151,43,0.2)' : 'transparent', color: period === p ? colors.gold : colors.muted, minHeight: '32px' }}>
            {p}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
        {[{ id: 'networth', label: 'Net Worth' }, { id: 'assets', label: 'Assets' }, { id: 'debt', label: 'Debt' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '6px 14px', fontSize: '14px', fontFamily: fonts.body, fontWeight: 600,
            border: `1px solid ${tab === t.id ? colors.borderGold : 'rgba(255,255,255,0.06)'}`,
            borderRadius: '2px', cursor: 'pointer', minHeight: '36px',
            background: tab === t.id ? 'rgba(201,151,43,0.1)' : 'transparent',
            color: tab === t.id ? colors.gold : colors.muted,
          }}>
            {t.label}
          </button>
        ))}
      </div>
      {(!history || history.length === 0) ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: colors.muted, fontFamily: fonts.body, fontSize: '16px' }}>Net worth builds up as you update your accounts</div>
      ) : (() => {
        const filtered = filterHistory(history, period)
        const aggregated = aggregateHistory(filtered, period)
        if (aggregated.length === 0) {
          return (
            <div style={{ textAlign: 'center', padding: '40px 0', color: colors.muted, fontFamily: fonts.body, fontSize: '16px', lineHeight: 1.5 }}>
              No snapshots in this period.<br />
              <span style={{ fontSize: '14px' }}>Try a longer range or update your account balances.</span>
            </div>
          )
        }
        return (
          <>
            <div style={{ position: 'relative', height: '220px' }}>
              <canvas ref={ref} role="img" aria-label="Net worth over time" />
            </div>
            {singlePoint && (
              <div style={{ marginTop: '10px', textAlign: 'center', color: colors.muted, fontFamily: fonts.body, fontSize: '16px', lineHeight: 1.4 }}>
                Tracking just started - your trend line builds as you log more snapshots
              </div>
            )}
          </>
        )
      })()}
    </div>
  )
}
